import {
  Box,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import Uppy, { Meta } from "@uppy/core";
import { t } from "i18next";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { debouncePerKey } from "./debouncePerKey";
import { IconType } from "react-icons";
import { FaArrowDown, FaArrowsAltV, FaArrowUp, FaFilter } from "react-icons/fa";
import {
  DocumentCategory,
  SignedDocumentDto,
  TextDocumentDto,
  useUpdateDocumentMetaMutation,
  useUpdateSignedDocumentMetaMutation,
  useUpdateTextDocumentMutation,
  ViewDocumentAsOwnerDto,
  ViewSharedDocumentDto,
} from "../../autogen/bff-api";
import {
  MainDocumentContext,
  mainDocumentOfSignedDocument,
} from "../../pages/contracts/edit/docs/signatures/CreateSignedDocument";
import { DocumentSignersModal } from "../../pages/contracts/edit/docs/signatures/DocumentSignersModal";
import { SendForSigningModal } from "../../pages/contracts/edit/docs/signatures/SendForSigningModal";
import { UploadRow } from "../../pages/contracts/edit/docs/UploadRow";
import { useApiError } from "../errors/useApiError";
import { CounterpartyContext } from "../types";
import { AnnouncementAttachmentRow } from "./AnnouncementAttachmentRow";
import { AttachmentsModal } from "./AttachmentsModal";
import { FencyChatWithDocumentModal } from "./FencyChatWithDocumentModal";
import { DocumentAnalysisModal } from "./DocumentAnalysisModal";
import { MergedDocumentsModal } from "./MergedDocumentsModal";
import { PreviewPdfModal } from "./PreviewPdfModal";
import { SignedDocumentRow } from "./SignedDocumentRow";
import { TextDocumentModal } from "./TextDocumentModal";
import { TextDocumentRow } from "./TextDocumentRow";
import { DropTarget } from "./UppyUploader/DropTarget";
import { FileUpload, IncompleteFileUpload } from "./UppyUploader/FileRow";
import {
  AnnouncementAttachment,
  isAnnouncementAttachment,
  isSignable,
  isSignedDocumentDto,
  isTextDocument,
  isViewDocumentAsOwnerDto,
  isViewSharedDocumentDto,
} from "./utils";
import { ViewUploadedDocumentRow } from "./ViewUploadedDocumentRow";

type CombinedDocument =
  | ViewSharedDocumentDto
  | ViewDocumentAsOwnerDto
  | SignedDocumentDto
  | TextDocumentDto
  | AnnouncementAttachment;

enum SortableColumn {
  Title = "Title",
  CreatedAt = "CreatedAt",
  CreatedBy = "CreatedBy",
}

type SortOrder = "asc" | "desc";
interface CombinedDocumentsSortState {
  column: SortableColumn;
  direction: SortOrder;
}

type PersonNameLike =
  | {
      firstName?: string | null;
      lastName?: string | null;
    }
  | null
  | undefined;

const getPersonFullName = (person: PersonNameLike) => {
  const firstName = person?.firstName?.trim() ?? "";
  const lastName = person?.lastName?.trim() ?? "";
  return [firstName, lastName]
    .filter((value) => value !== "")
    .join(" ")
    .trim();
};

const getCreatedBySortValue = (document: CombinedDocument): string => {
  if (isAnnouncementAttachment(document)) return "";
  if (isTextDocument(document)) return getPersonFullName(document.createdBy);
  if (isViewDocumentAsOwnerDto(document)) return getPersonFullName(document.uploadedBy);
  if (isSignedDocumentDto(document)) return getPersonFullName(document.sentForSigningBy);
  return "";
};

const getTitleSortValue = (document: CombinedDocument) => document.name.toLocaleLowerCase();

const getCreatedAtSortValue = (document: CombinedDocument) => {
  const timestamp = isTextDocument(document)
    ? document.createdAt
    : isAnnouncementAttachment(document)
      ? document.publishedAt
      : document.uploadedAt;
  const parsedValue = timestamp ? Date.parse(timestamp) : Number.NaN;
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

export const isAiAnalysable = (document: SignedDocumentDto | ViewDocumentAsOwnerDto) =>
  isSignedDocumentDto(document) || (isViewDocumentAsOwnerDto(document) && document.fileExtension === ".pdf");

export const CombinedDocuments = ({
  uploadedDocuments,
  setUploadedDocuments,
  removeUploadedDocument,
  signedDocuments,
  setSignedDocuments,
  textDocuments = [],
  setTextDocuments,
  announcementAttachments = [],
  emptyState,
  isAiChatEnabled = false,
  documentSignatureConfig,
  canSendForSigning = false,
  canEditDocumentNames = false,
  canDeleteDocuments = false,
  canEditTextDocuments = false,
  createTextDocument,
  isCreatingTextDocument = false,
  isLoading = false,
  documentUploadConfig,
  sortConfig,
  isTagsColumnVisible,
  isCategoryColumnVisible = false,
  isCreatedByColumnVisible = true,
  canEditTags = false,
  canEditCategory = false,
  refetchDocuments,
  contractId,
}: {
  uploadedDocuments: (ViewSharedDocumentDto | ViewDocumentAsOwnerDto)[];
  setUploadedDocuments?: Dispatch<SetStateAction<ViewDocumentAsOwnerDto[]>>;
  removeUploadedDocument?: (document: ViewDocumentAsOwnerDto) => Promise<void>;
  signedDocuments?: SignedDocumentDto[];
  setSignedDocuments?: Dispatch<SetStateAction<SignedDocumentDto[]>>;
  textDocuments?: TextDocumentDto[];
  setTextDocuments?: Dispatch<SetStateAction<TextDocumentDto[]>>;
  announcementAttachments?: AnnouncementAttachment[];
  emptyState?: ReactNode;
  isAiChatEnabled?: boolean;
  documentSignatureConfig?: {
    contractId: string;
    counterpartyContext: CounterpartyContext;
  };
  canSendForSigning?: boolean;
  canEditDocumentNames?: boolean;
  canDeleteDocuments?: boolean;
  canEditTextDocuments?: boolean;
  createTextDocument?: () => Promise<TextDocumentDto>;
  isCreatingTextDocument?: boolean;
  isLoading?: boolean;
  documentUploadConfig?: {
    dropTargetId: string;
    uppy: Uppy<Meta, Record<string, never>>;
    dropTargetHelperText: string;
    uploads: IncompleteFileUpload[];
    removeUpload: (file: FileUpload) => Promise<void>;
  };
  sortConfig?: {
    isTitleSortEnabled: boolean;
    isCreatedAtSortEnabled: boolean;
    isCreatedBySortEnabled: boolean;
  };
  isTagsColumnVisible: boolean;
  isCategoryColumnVisible?: boolean;
  isCreatedByColumnVisible?: boolean;
  canEditTags?: boolean;
  canEditCategory?: boolean;
  refetchDocuments?: () => void | Promise<void>;
  contractId?: string;
}) => {
  const displayer = useApiError();

  const [updateDocumentMeta] = useUpdateDocumentMetaMutation();
  const [updateTextDocument] = useUpdateTextDocumentMutation();
  const [updateSignedDocumentMeta] = useUpdateSignedDocumentMetaMutation();

  const [textDocumentToView, setTextDocumentToView] = useState<TextDocumentDto>();
  const [documentToSign, setDocumentToSign] = useState<MainDocumentContext>();
  const [documentToResend, setDocumentToResend] = useState<SignedDocumentDto>();
  const [documentForAnalysis, setDocumentForAnalysis] = useState<ViewDocumentAsOwnerDto | SignedDocumentDto>();
  const [documentForSignersModal, setDocumentForSignersModal] = useState<SignedDocumentDto>();
  const [documentForAttachmentsModal, setDocumentForAttachmentsModal] = useState<SignedDocumentDto>();
  const [documentForMergedDocumentsModal, setDocumentForMergedDocumentsModal] = useState<SignedDocumentDto>();
  const [documentForFencyModal, setDocumentForFencyModal] = useState<ViewDocumentAsOwnerDto | SignedDocumentDto>();
  const [selectedTags, setSelectedTags] = useState<string[]>();

  // When the parent refetches documents, sync the Fency modal's document with the fresh data
  // so it has the updated fencyContentStatus (e.g. after sync completes).
  useEffect(() => {
    if (!documentForFencyModal) return;
    const fencyCompatibleDocs: (ViewDocumentAsOwnerDto | SignedDocumentDto)[] = [
      ...(uploadedDocuments ?? []).filter(isViewDocumentAsOwnerDto),
      ...(signedDocuments ?? []),
    ];
    const freshDoc = fencyCompatibleDocs.find((d) => d.id === documentForFencyModal.id);
    if (freshDoc && freshDoc.fencyContentStatus !== documentForFencyModal.fencyContentStatus) {
      setDocumentForFencyModal(freshDoc);
    }
  }, [uploadedDocuments, signedDocuments, documentForFencyModal]);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>();

  const signableDocuments = useMemo(() => {
    return uploadedDocuments.filter((doc) => isSignable(doc));
  }, [uploadedDocuments]);

  const canResend = (signedDocument: SignedDocumentDto) =>
    canSendForSigning && mainDocumentOfSignedDocument(signedDocument, textDocuments) !== undefined;
  const startResend = (signedDocument: SignedDocumentDto) => {
    setDocumentForSignersModal(undefined);
    setDocumentToResend(signedDocument);
  };
  const resendMainDocument = documentToResend && mainDocumentOfSignedDocument(documentToResend, textDocuments);
  const resentAs = (signedDocument: SignedDocumentDto) =>
    signedDocuments?.find((d) => d.id === signedDocument.resentAsSignedDocumentId);

  const combinedDocuments: CombinedDocument[] = useMemo(() => {
    return [...uploadedDocuments, ...(signedDocuments ?? []), ...textDocuments, ...announcementAttachments];
  }, [announcementAttachments, signedDocuments, textDocuments, uploadedDocuments]);

  const {
    isTitleSortEnabled = false,
    isCreatedAtSortEnabled = false,
    isCreatedBySortEnabled = false,
  } = sortConfig ?? {};
  const [sortState, setSortState] = useState<CombinedDocumentsSortState>();

  const isSortEnabled = (column: SortableColumn) => {
    if (column === SortableColumn.Title) return isTitleSortEnabled;
    if (column === SortableColumn.CreatedAt) return isCreatedAtSortEnabled;
    if (column === SortableColumn.CreatedBy) return isCreatedBySortEnabled;
    else throw new Error("Unknown column or column not enabled for sort");
  };

  const activeSortState = sortState && isSortEnabled(sortState.column) ? sortState : undefined;

  const columnCount =
    3 + (isCreatedByColumnVisible ? 1 : 0) + (isTagsColumnVisible ? 1 : 0) + (isCategoryColumnVisible ? 1 : 0);

  const sortedDocuments = useMemo(() => {
    if (!activeSortState) return combinedDocuments;

    const directionMultiplier = activeSortState.direction === "asc" ? 1 : -1;
    const sorted = [...combinedDocuments].sort((docA, docB) => {
      if (activeSortState.column === SortableColumn.CreatedAt) {
        const difference = getCreatedAtSortValue(docA) - getCreatedAtSortValue(docB);
        if (difference === 0) return 0;
        return difference > 0 ? directionMultiplier : -directionMultiplier;
      }

      const accessor = activeSortState.column === SortableColumn.Title ? getTitleSortValue : getCreatedBySortValue;
      return accessor(docA).localeCompare(accessor(docB), undefined, { sensitivity: "base" }) * directionMultiplier;
    });

    return sorted;
  }, [combinedDocuments, activeSortState]);

  const getSortIcon = (column: SortableColumn): IconType => {
    if (!sortState || sortState.column !== column) return FaArrowsAltV;
    return sortState.direction === "asc" ? FaArrowUp : FaArrowDown;
  };

  const getSortIconColor = (column: SortableColumn) => (sortState?.column === column ? "smPrimary" : "smMuted");

  const handleSortToggle = (column: SortableColumn) => {
    if (!isSortEnabled(column)) return;
    setSortState((previousState) => {
      if (!previousState || previousState.column !== column) return { column, direction: "asc" };
      if (previousState.direction === "asc") return { column, direction: "desc" };
      return undefined;
    });
  };

  const documentToSignContext = documentToSign?.documentId
    ? { documentId: documentToSign.documentId }
    : documentToSign?.textDocument
      ? { textDocument: documentToSign.textDocument }
      : undefined;

  const debouncedOnUpdateDocumentMeta = useMemo(
    () =>
      debouncePerKey(
        ({ docId, name, tags }: { docId: string; name: string; tags?: string[] }) => {
          updateDocumentMeta({
            docId,
            updateDocumentMetaRequest: { name, tags },
          });
        },
        ({ docId }) => docId,
        300,
      ),
    [updateDocumentMeta]
  );

  const onUpdateCategory = useCallback(
    (docId: string, category: DocumentCategory | null) => {
      const previousDoc = uploadedDocuments.find((d) => d.id === docId);
      const previousCategory = previousDoc && "category" in previousDoc ? previousDoc.category : undefined;
      setUploadedDocuments?.((prev) =>
        prev.map((doc) => (doc.id === docId ? { ...doc, category: category ?? undefined } : doc))
      );
      return updateDocumentMeta({
        docId,
        updateDocumentMetaRequest: { category },
      })
        .unwrap()
        .catch((err) => {
          setUploadedDocuments?.((prev) =>
            prev.map((doc) => (doc.id === docId ? { ...doc, category: previousCategory } : doc))
          );
          displayer.triggerSoft(err);
        });
    },
    [updateDocumentMeta, displayer, setUploadedDocuments, uploadedDocuments]
  );

  const debouncedOnUpdateSignedDocumentMeta = useMemo(
    () =>
      debouncePerKey(
        ({ docId, tags }: { docId: string; tags: string[] }) => {
          updateSignedDocumentMeta({
            docId,
            updateSignedDocumentMetaRequest: { tags },
          });
        },
        ({ docId }) => docId,
        0,
      ),
    [updateSignedDocumentMeta]
  );

  const debouncedUpdateTextDocument = useMemo(
    () =>
      debouncePerKey(
        ({
          textDocumentId,
          name,
          text,
          tags,
        }: {
          textDocumentId: string;
          name?: string;
          text?: string;
          tags?: string[];
        }) => {
          updateTextDocument({
            updateTextDocumentRequest: {
              id: textDocumentId,
              name,
              text,
              tags,
            },
          })
            .unwrap()
            .then(
              (saved) =>
                setTextDocuments?.((current) =>
                  current.map((doc) => (doc.id === saved.id ? { ...doc, modifiedAt: saved.modifiedAt } : doc)),
                ),
              () => undefined,
            );
        },
        ({ textDocumentId }) => textDocumentId,
        300,
      ),
    [updateTextDocument, setTextDocuments]
  );

  const onUpdateDocumentMeta = ({
    document,
    name,
    tags,
  }: {
    document: ViewDocumentAsOwnerDto;
    name: string;
    tags: string[];
  }) => {
    debouncedOnUpdateDocumentMeta({ docId: document.id, name, tags });
    if (!setUploadedDocuments) throw Error("Cannot update uploaded documents state without setter");
    setUploadedDocuments((uploadedDocuments) => {
      const updatedDocs = [...uploadedDocuments];
      const index = uploadedDocuments.findIndex((d) => d.id === document.id);
      if (index !== -1) updatedDocs[index] = { ...document, name, tags };
      return updatedDocs;
    });
  };

  const onUpdateSignedDocumentTags = ({ document, tags }: { document: SignedDocumentDto; tags: string[] }) => {
    debouncedOnUpdateSignedDocumentMeta({ docId: document.id, tags });
    if (!setSignedDocuments) throw Error("Cannot update uploaded documents state without setter");
    setSignedDocuments((signedDocuments) => {
      const updatedDocs = [...signedDocuments];
      const index = signedDocuments.findIndex((d) => d.id === document.id);
      if (index !== -1) updatedDocs[index] = { ...document, tags };
      return updatedDocs;
    });
  };

  const onUpdateTextDocument = ({
    document,
    name,
    tags,
  }: {
    document: TextDocumentDto;
    name: string;
    tags: string[];
  }) => {
    debouncedUpdateTextDocument({ textDocumentId: document.id, name, tags });
    if (!setTextDocuments) throw new Error("Cannot update text documents state without setter");
    setTextDocuments((textDocuments) => {
      const updatedTextDocs = [...textDocuments];
      const index = textDocuments.findIndex((d) => d.id === document.id);
      if (index !== -1) updatedTextDocs[index] = { ...document, name, tags, modifiedAt: new Date().toISOString() };
      return updatedTextDocs;
    });
  };

  const onDeleteUploadedDocument = async (document: ViewDocumentAsOwnerDto) => {
    if (!removeUploadedDocument) {
      throw new Error("Delete handler for uploaded documents not defined - could not delete");
    }
    await removeUploadedDocument(document);
    setUploadedDocuments?.((docs) => docs.filter((d) => d.id !== document.id));
  };

  const onCreateTextDocument = async () => {
    if (!createTextDocument) throw new Error("Cannot create text document without mutation");
    const textDocument = await createTextDocument().catch((e) => {
      displayer.trigger(e);
      throw e;
    });
    setTextDocumentToView(textDocument);
  };

  const tags = useMemo(() => {
    const uploadedDocumentTags = uploadedDocuments
      .filter((document) => isViewDocumentAsOwnerDto(document))
      .flatMap((document) => (document as ViewDocumentAsOwnerDto).tags);
    const textDocumentTags = textDocuments.flatMap((document) => document.tags);
    const signedDocumentTags = signedDocuments?.flatMap((document) => document.tags) ?? [];
    return Array.from(new Set([...uploadedDocumentTags, ...textDocumentTags, ...signedDocumentTags])).sort((a, b) =>
      a > b ? 1 : -1
    );
  }, [signedDocuments, textDocuments, uploadedDocuments]);

  useEffect(() => {
    setSelectedTags((selectedTags) => selectedTags?.filter((t) => tags.includes(t)));
  }, [tags]);

  const filteredDocuments = useMemo(() => {
    if (!selectedTags?.length) return sortedDocuments;
    return sortedDocuments.filter(
      (document) =>
        (isViewDocumentAsOwnerDto(document) || isTextDocument(document) || isSignedDocumentDto(document)) &&
        document.tags.some((t) => selectedTags?.includes(t))
    );
  }, [selectedTags, sortedDocuments]);

  return (
    <>
      {documentForAnalysis && (
        <DocumentAnalysisModal document={documentForAnalysis} setDocumentForAnalysis={setDocumentForAnalysis} />
      )}
      {documentForFencyModal && (
        <FencyChatWithDocumentModal
          document={documentForFencyModal}
          onClose={() => setDocumentForFencyModal(undefined)}
          refetchDocuments={refetchDocuments}
        />
      )}
      {textDocumentToView && (
        <TextDocumentModal
          textDocument={textDocumentToView}
          contractId={contractId}
          onClose={() => setTextDocumentToView(undefined)}
          isUpdating={false}
          editable={canEditTextDocuments}
          onUpdate={({ title, content, tags }) => {
            debouncedUpdateTextDocument({ textDocumentId: textDocumentToView.id, name: title, text: content, tags });
            setTextDocuments?.((current) =>
              current.map((doc) =>
                doc.id === textDocumentToView.id
                  ? {
                      ...doc,
                      name: title ?? doc.name,
                      text: content ?? doc.text,
                      tags: tags ?? doc.tags,
                      modifiedAt: new Date().toISOString(),
                    }
                  : doc,
              ),
            );
          }}
        />
      )}
      {documentToSignContext && documentSignatureConfig && (
        <SendForSigningModal
          contractId={documentSignatureConfig.contractId}
          mainDocument={documentToSignContext}
          textDocuments={textDocuments}
          documents={signableDocuments}
          onClose={() => setDocumentToSign(undefined)}
          counterpartyContext={documentSignatureConfig.counterpartyContext}
        />
      )}
      {documentToResend && resendMainDocument && documentSignatureConfig && (
        <SendForSigningModal
          contractId={documentSignatureConfig.contractId}
          mainDocument={resendMainDocument}
          textDocuments={textDocuments}
          documents={signableDocuments}
          resendOf={documentToResend}
          onClose={() => setDocumentToResend(undefined)}
          counterpartyContext={documentSignatureConfig.counterpartyContext}
        />
      )}
      {documentForSignersModal && documentSignatureConfig && (
        <DocumentSignersModal
          contractId={documentSignatureConfig.contractId}
          signedDocument={documentForSignersModal}
          resentAs={resentAs(documentForSignersModal)}
          onClose={() => setDocumentForSignersModal(undefined)}
          onResend={canResend(documentForSignersModal) ? startResend : undefined}
          onOpenSignedDocument={setDocumentForSignersModal}
        />
      )}
      {documentForAttachmentsModal && (
        <AttachmentsModal
          signedDocument={documentForAttachmentsModal}
          onClose={() => setDocumentForAttachmentsModal(undefined)}
        />
      )}
      {documentForMergedDocumentsModal && (
        <MergedDocumentsModal
          signedDocument={documentForMergedDocumentsModal}
          onClose={() => setDocumentForMergedDocumentsModal(undefined)}
        />
      )}
      {previewPdfUrl && <PreviewPdfModal url={previewPdfUrl} onClose={() => setPreviewPdfUrl(undefined)} />}
      {documentUploadConfig && (
        <>
          <DropTarget
            targetId={documentUploadConfig.dropTargetId}
            uppy={documentUploadConfig.uppy}
            helperText={documentUploadConfig.dropTargetHelperText ?? t("Upload documents")}
            createTextDocument={canEditTextDocuments ? onCreateTextDocument : undefined}
            isCreatingTextDocument={isCreatingTextDocument}
          />
          {documentUploadConfig.uploads.length ? (
            <Box pb="8">
              <Table variant="simple" size="sm">
                <Tbody>
                  {documentUploadConfig.uploads.map((d) => (
                    <UploadRow key={d.uploadId} file={d} name={d.name} remove={documentUploadConfig.removeUpload} />
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : null}
        </>
      )}
      {isTagsColumnVisible && (
        <Flex
          alignItems="center"
          columnGap="1"
          rowGap="1"
          flexWrap="wrap"
          width="full"
          border="1px solid"
          borderColor="smBorder"
          rounded="lg"
          p="4"
          mb="4"
        >
          <Icon as={FaFilter} color="smInfo" />
          <Text fontSize="sm">{t("Filter")}:</Text>
          {!isLoading && (
            <>
              {tags.length ? (
                tags.map((tag, i) => (
                  <Tag
                    key={`${tag}-${i}`}
                    size="sm"
                    cursor="pointer"
                    backgroundColor={selectedTags?.includes(tag) ? "smSecondary" : ""}
                    color={selectedTags?.includes(tag) ? "smBackground" : ""}
                    onClick={() => setSelectedTags((tags) => [...(tags ?? []), tag])}
                  >
                    <TagLabel>{tag}</TagLabel>
                    {selectedTags?.includes(tag) && (
                      <TagCloseButton
                        aria-label="remove tag"
                        onClick={(e) => {
                          e.stopPropagation();
                          const updatedTags = selectedTags?.filter((t) => t !== tag);
                          setSelectedTags(updatedTags);
                        }}
                      />
                    )}
                  </Tag>
                ))
              ) : (
                <Text fontSize="sm" color="smMuted">
                  {t("No tags yet")}.
                </Text>
              )}
            </>
          )}
        </Flex>
      )}
      {!isLoading && combinedDocuments.length === 0 && emptyState ? (
        emptyState
      ) : (
        <Box
          width="full"
          maxWidth="full"
          overflowX="auto"
          border="1px solid"
          borderColor="smBorder"
          borderRadius="md"
          pb="2.5"
        >
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>
                  <Flex alignItems="center">
                    {t("Name")}
                    {isTitleSortEnabled ? (
                      <IconButton
                        aria-label="sort by title"
                        variant="ghost"
                        size="sm"
                        icon={
                          <Icon
                            as={getSortIcon(SortableColumn.Title)}
                            color={getSortIconColor(SortableColumn.Title)}
                            boxSize="3"
                          />
                        }
                        onClick={() => handleSortToggle(SortableColumn.Title)}
                        aria-pressed={sortState?.column === SortableColumn.Title}
                      />
                    ) : null}
                  </Flex>
                </Th>
                <Th minWidth="48">
                  {t("Created at")}
                  {isCreatedAtSortEnabled ? (
                    <IconButton
                      aria-label="sort by created at"
                      variant="ghost"
                      size="sm"
                      icon={
                        <Icon
                          as={getSortIcon(SortableColumn.CreatedAt)}
                          color={getSortIconColor(SortableColumn.CreatedAt)}
                          boxSize="3"
                        />
                      }
                      onClick={() => handleSortToggle(SortableColumn.CreatedAt)}
                      aria-pressed={sortState?.column === SortableColumn.CreatedAt}
                    />
                  ) : null}
                </Th>
                {isCreatedByColumnVisible && (
                  <Th minWidth="56">
                    {t("Created by")}
                    {isCreatedBySortEnabled ? (
                      <IconButton
                        aria-label="sort by created by"
                        variant="ghost"
                        size="sm"
                        icon={
                          <Icon
                            as={getSortIcon(SortableColumn.CreatedBy)}
                            color={getSortIconColor(SortableColumn.CreatedBy)}
                            boxSize="3"
                          />
                        }
                        onClick={() => handleSortToggle(SortableColumn.CreatedBy)}
                        aria-pressed={sortState?.column === SortableColumn.CreatedBy}
                      />
                    ) : null}
                  </Th>
                )}
                {isTagsColumnVisible && <Th minWidth="48">{t("Tags")}</Th>}
                {isCategoryColumnVisible && <Th minWidth="72">{t("Category")}</Th>}
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {isLoading &&
                Array.from({ length: 3 }).map((_, rowIndex) => (
                  <Tr key={rowIndex}>
                    {Array.from({ length: columnCount - 1 }).map((_, cellIndex) => (
                      <Td key={cellIndex}>
                        <Skeleton
                          height="8"
                          width={cellIndex === 0 ? "80" : cellIndex === columnCount - 2 ? "20" : "40"}
                          rounded="md"
                          startColor="smBackground"
                          endColor="smBackgroundSecondary"
                        />
                      </Td>
                    ))}
                    <Td>
                      <Flex columnGap={1}>
                        {Array.from({ length: 3 }).map((_, iconIndex) => (
                          <Skeleton
                            key={iconIndex}
                            height="8"
                            width="8"
                            rounded="md"
                            startColor="smBackground"
                            endColor="smBackgroundSecondary"
                          />
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              {!isLoading && combinedDocuments.length === 0 && (
                <Tr>
                  <Td
                    colSpan={columnCount}
                    width="full"
                    textAlign="center"
                  >
                    {t("No documents")}.
                  </Td>
                </Tr>
              )}
              {!isLoading &&
                filteredDocuments.map((doc) => {
                  if (isAnnouncementAttachment(doc)) {
                    return (
                      <AnnouncementAttachmentRow
                        key={doc.id}
                        doc={doc}
                        isTagsColumnVisible={isTagsColumnVisible}
                        isCategoryColumnVisible={isCategoryColumnVisible}
                        isCreatedByColumnVisible={isCreatedByColumnVisible}
                      />
                    );
                  }
                  if (isTextDocument(doc)) {
                    return (
                      <TextDocumentRow
                        key={doc.id}
                        doc={doc}
                        setTextDocumentToView={setTextDocumentToView}
                        canEditName={canEditDocumentNames}
                        canEditTags={canEditTags}
                        onUpdateDocumentMeta={onUpdateTextDocument}
                        canDeleteDocument={canDeleteDocuments}
                        setDocumentToSign={documentSignatureConfig && canSendForSigning ? setDocumentToSign : undefined}
                        isTagsColumnVisible={isTagsColumnVisible}
                        isCategoryColumnVisible={isCategoryColumnVisible}
                        isCreatedByColumnVisible={isCreatedByColumnVisible}
                      />
                    );
                  }
                  if (isViewDocumentAsOwnerDto(doc) || isViewSharedDocumentDto(doc)) {
                    return (
                      <ViewUploadedDocumentRow
                        key={doc.id}
                        doc={doc}
                        canEditName={canEditDocumentNames}
                        canEditTags={canEditTags}
                        canEditCategory={canEditCategory}
                        onUpdateDocumentMeta={onUpdateDocumentMeta}
                        onUpdateCategory={onUpdateCategory}
                        isAiChatEnabled={isAiChatEnabled}
                        setDocumentForAnalysis={setDocumentForAnalysis}
                        setDocumentForFencyModal={setDocumentForFencyModal}
                        canDeleteDocument={canDeleteDocuments}
                        deleteDocument={onDeleteUploadedDocument}
                        setDocumentToSign={documentSignatureConfig && canSendForSigning ? setDocumentToSign : undefined}
                        isTagsColumnVisible={isTagsColumnVisible}
                        isCategoryColumnVisible={isCategoryColumnVisible}
                        isCreatedByColumnVisible={isCreatedByColumnVisible}
                        setPreviewPdfUrl={setPreviewPdfUrl}
                      />
                    );
                  }
                  if (isSignedDocumentDto(doc)) {
                    return (
                      <SignedDocumentRow
                        key={doc.id}
                        doc={doc}
                        isAiChatEnabled={isAiChatEnabled}
                        setDocumentForAnalysis={setDocumentForAnalysis}
                        setDocumentForFencyModal={setDocumentForFencyModal}
                        setDocumentForAttachmentsModal={setDocumentForAttachmentsModal}
                        setDocumentForMergedDocumentsModal={setDocumentForMergedDocumentsModal}
                        setDocumentForSignersModal={setDocumentForSignersModal}
                        isTagsColumnVisible={isTagsColumnVisible}
                        isCategoryColumnVisible={isCategoryColumnVisible}
                        canEditTags={canEditTags}
                        onUpdateTags={onUpdateSignedDocumentTags}
                        isCreatedByColumnVisible={isCreatedByColumnVisible}
                        setPreviewPdfUrl={setPreviewPdfUrl}
                      />
                    );
                  }
                })}
            </Tbody>
          </Table>
        </Box>
      )}
    </>
  );
};
